
import { mapMutations, mapGetters, mapActions } from "vuex";
import { format } from "date-fns";
import UiButton from "@/components/UI/UiButton.vue";
import UiReadMore from "@/components/UI/UiReadMore.vue";
import useProductMixin from "~/mixins/useProductMixin";
import ProductMediaDesktop from "@/components/pages/product/ProductMediaDesktop";
import ProductMediaMobileSticky from "@/components/pages/product/ProductMediaMobileSticky";
import FilterSlider from "@/components/pages/product/FilterSlider";
import FilterBtn from "@/components/pages/product/FilterBtn";
import ProductCertificateLogos from "@/components/pages/product/ProductCertificateLogos.vue";
import {
  DataURIToBlob,
  getProductImages,
  mergeImages,
  replacePlaceholder,
} from "@/utils";
import googleAnalyticsEvents from "~/mixins/googleAnalyticsEvents";
import facebookPixelEvents from "~/mixins/facebookPixelEvents";
import UiSpinner from "@/components/UI/UiSpinner.vue";
import ProductDetails from "@/components/pages/product/ProductDetails.vue";
import FilterSelect from "@/components/pages/product/FilterSelect";
import PerfectComplimentBlock from "~/components/pages/product/PerfectComplimentBlock.vue";
import RelatedProductsBlockPDP from "~/components/blocks/RelatedProductsBlockPDP.vue";
import jsonLdMixin from "~/mixins/jsonLdMixin";
import useEvents from "~/mixins/useEvents";
import useZohoChat from "~/mixins/useZohoChat";
import SalePDPBlock from "~/components/blocks/CustomBlocks/SalePDPBlock.vue";
import DiamondKnowledge from "~/components/pages/product/DiamondKnowledge";
import UiIcon from "~/components/UI/UiIcon.vue";
import {
  CERTIFICATE_PROVIDERS,
  certificationSkus,
  collectionSlugs,
  productMaterialsSKU,
} from "~/config/_constants";
import FreeSizerBlock from "~/components/blocks/CustomBlocks/FreeSizerBlock.vue";
import CertificationBlock from "~/components/blocks/CustomBlocks/CertificationBlock.vue";

const HIDDEN_PROPERTY_GROUP_BY_COLLECTION = {
  ORIGIN_DMND: ["dn"],
  PUVOD_DIAMANTU: ["dn"],
};

export default {
  components: {
    UiIcon,
    SalePDPBlock,
    DiamondKnowledge,
    FreeSizerBlock,
    CertificationBlock,
    PerfectComplimentBlock,
    RelatedProductsBlockPDP,
    UiButton,
    UiReadMore,
    ProductMediaDesktop,
    FilterSlider,
    FilterBtn,
    ProductCertificateLogos,
    UiSpinner,
    ProductDetails,
    FilterSelect,
    ProductMediaMobileSticky,
  },
  mixins: [
    useProductMixin,
    googleAnalyticsEvents,
    facebookPixelEvents,
    jsonLdMixin,
    useEvents,
    useZohoChat,
  ],
  props: {
    // productData: {
    //   type: Object,
    //   default: () => {},
    // },
    productInfo: {
      type: Object,
      default: () => {},
    },
    parseAdditionalProperties: {
      type: Function,
      required: true,
    },
    hasDiamondKnowledgeCollection: {
      type: Boolean,
      default: false,
    },
    salePDPData: {
      type: Object,
      default: () => {},
    },
    freeSizerBlock: {
      type: Object,
      default: () => null,
    },
    certificationBlock: {
      type: Object,
      default: () => null,
    },
    setRecentlyViewed: {
      type: Function,
      required: true,
    },
    isRing: {
      type: Boolean,
      default: false,
    },
    relatedProductsList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {
        color: "black",
        size: "100",
        amount: 1,
      },
      isWishlist: false,
      isCompare: false,
      wishlistKey: "conf-m8ht77-wishlist",
      compareKey: "conf-m8ht77-compare",
      productId: "jk1hkj13h31-1331nmb13",
      result: [],
      additionalOptions: [],
      configurationLoading: false,
      configurationLoaded: false,
      addToBagLoading: false,

      filterSliderViewTypes: ["round", "icon"],
      filterSelectViewType: "select",
      customEvent: null,
      resizeObserver: null,
      sizeSKUs: [
        "VELKOST",
        "VELIKOST",
        "ROZMIAR",
        "ROZMIAR_BRANSOLETKI",
        "VELIKOST_NARAMKU",
        "VELKOST_NARAMKU",
      ],
      certificationSkus,
      isMounted: false,
      descriptionKeyIncrement: 0,
    };
  },
  async fetch() {
    await this.getProductConfiguration();
    this.setMaterials();
    this.updateJsonSchemaData();
  },
  computed: {
    ...mapGetters({
      appBreakpoints: "general/getAppBreakpoints",
      getMaterialsCategories: "general/getMaterialsCategories",
      cartId: "getCartId",
      getSystemDateFormat: "general/getSystemDateFormat",
    }),
    settings() {
      return this.$store.state.general.settings;
    },
    filteredMaterialsByCategories() {
      const globalsSortOrder = true;
      if (!this.productData) {
        return [];
      }

      const materialsByCategories = this.productData.material_categories.reduce(
        (acc, el) => {
          const currentCategory = this.getMaterialsCategories?.find(
            (it) => it.id === el.id
          );

          if (currentCategory) {
            acc.push({
              ...currentCategory,
              materials: this.productData.materials.reduce((acc, material) => {
                if (material.material_category_id === el.id) {
                  acc.push({
                    ...material,
                  });
                }
                return acc;
              }, []),
            });
          }
          return acc;
        },
        []
      );

      return globalsSortOrder
        ? materialsByCategories.sort(
            (a, b) => (a?.sort_order || 0) - (b?.sort_order || 0)
          )
        : materialsByCategories;
    },
    currentSku: {
      get() {
        return (
          this.$route.query.configuration_id ||
          this.productData?.default_sku ||
          ""
        );
      },
      async set(val) {
        await this.$router.replace({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            configuration_id: val,
          },
        });
      },
    },
    materialsQuery: {
      get() {
        if (Array.isArray(this.$route.query.materials)) {
          return this.$route.query.materials || [];
        } else if (this.$route.query.materials) {
          return this.$route.query.materials
            ? [this.$route.query.materials]
            : [];
        } else {
          return (
            Object.values(this.additionalOptions)?.map((el) => el?.sku) || []
          );
        }
      },
      async set(val) {
        await this.$router.replace({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            materials: val,
          },
        });
      },
    },
    canSendGoogleAnalyticsStatistic() {
      return (
        this.integrationCallbackStatuses.TYPE_GOOGLE_ANALYTICS &&
        this.configurationLoaded
      );
    },
    canSendFBStatistic() {
      return (
        this.integrationCallbackStatuses.TYPE_FACEBOOK_PIXEL &&
        this.configurationLoaded
      );
    },
    allSelectedMaterials() {
      return {
        ...this.result,
        ...this.additionalOptions,
      };
    },
    detailsMapedDataWithExtraData() {
      const extraData = [];
      const isCertificateAvailable =
        this.isCertificateIgi || this.isCertificateGia;

      if (
        isCertificateAvailable &&
        this.configurationInfo?.center_stone_cert_url
      ) {
        extraData.push({
          property: this.$t("certificateIssuer"),
          value: this.certificateProvider,
        });

        extraData.push({
          property: this.$t("certificateUrl"),
          value: this.certificateUrl,
        });
      }

      return [...this.detailsMapedData, ...extraData];
    },
    detailsMapedData() {
      if (
        !this.productData?.mixedTranslation?.content?.details_map &&
        !this.configurationInfo?.mixedTranslation?.additional_details
      ) {
        return [];
      }
      if (this.configurationInfo?.mixedTranslation?.additional_details) {
        const additionalDetails =
          this.configurationInfo.mixedTranslation.additional_details;
        return this.parseAdditionalProperties(additionalDetails);
      }

      return this.productData?.mixedTranslation?.content?.details_map.map(
        (el) => {
          return {
            property: el.property,
            value: replacePlaceholder(
              el.value,
              (materialSKU) =>
                this.allSelectedMaterials?.[
                  this.materialCategoriesDictionary?.[materialSKU]
                ]?.mixedTranslation?.title || ""
            ),
          };
        }
      );
    },
    dynamicProductTitle() {
      // used in mixin
      return replacePlaceholder(
        this.productData?.mixedTranslation?.dynamic_title || "",
        (materialSKU) =>
          this.allSelectedMaterials?.[
            this.materialCategoriesDictionary?.[materialSKU]
          ]?.mixedTranslation?.title || ""
      );
    },
    dynamicProductShortDescription() {
      const productShortDescription = replacePlaceholder(
        this.productData?.mixedTranslation?.short_description || "",
        (materialSKU) =>
          this.allSelectedMaterials?.[
            this.materialCategoriesDictionary?.[materialSKU]
          ]?.mixedTranslation?.title || ""
      );
      const configurationDescription =
        this.configurationInfo?.mixedTranslation?.description || "";
      return configurationDescription || productShortDescription;
    },
    readyToSetRecentlyView() {
      return this.configurationInfo && this.isMounted;
    },
    isForceToPay() {
      return this.$route.query?.["force-to-pay"] === "1" && this.isReadyToPay;
    },
    isReadyToPay() {
      return !!this.productInfo && !!this.configurationInfo && !!this.cartId;
    },
    mainImage() {
      const images = (this.configurationInfo?.medias || []).filter((item) =>
        item.position.includes("PDP")
      );
      return images[0]?.file;
    },
    hiddenPropertyGroupByCollection() {
      if (this.$config?.NAT_DIA_VISIBLE) {
        return {};
      }
      return HIDDEN_PROPERTY_GROUP_BY_COLLECTION;
    },
    hasReadyToShipCollection() {
      return (this.productData?.collections || []).some(
        (collection) => collection.slug === collectionSlugs.readyToShip
      );
    },
    estimateShipping() {
      const shipDate = this.configurationInfo?.ship_date;
      if (!shipDate) {
        return this.$t("shippingDateNotAvailable");
      }
      try {
        return format(new Date(shipDate), this.getSystemDateFormat);
      } catch (error) {
        console.error("Error formatting ship date:", error);
        return this.$t("shippingDateNotAvailable");
      }
    },
    returnDays() {
      const hasNatMaterial = Object.values(this.allSelectedMaterials).some(
        (material) => material?.sku === productMaterialsSKU.nat
      );
      return hasNatMaterial ? 15 : 30;
    },
    returnsLink() {
      const locale = this.$config.LOCALE?.toLowerCase();

      const linksMap = {
        cs: "/vraceni-a-vymena",
        pl: "/zwracanie-i-wymiana",
        de: "/rucksendung-umtausch",
        es: "/devoluciones-y-cambios",
        nl: "/retourneren-ruilen",
      };

      return linksMap[locale] || "/vratky-a-vymeny";
    },
    certificateUrl() {
      let certificateUrl = this.configurationInfo?.center_stone_cert_url || "";
      if (!certificateUrl) {
        return "";
      }
      if (this.isCertificateIgi) {
        certificateUrl = `https://www.igi.org/Verify-Your-Report/?r=${certificateUrl}`;
      } else if (this.isCertificateGia) {
        certificateUrl = `https://www.gia.edu/report-check?reportno=${certificateUrl}`;
      }
      return certificateUrl;
    },
    certificateProvider() {
      return (
        this.configurationInfo?.center_stone_cert_provider || ""
      ).toUpperCase();
    },
    isCertificateIgi() {
      return this.certificateProvider === CERTIFICATE_PROVIDERS.IGI;
    },
    isCertificateGia() {
      return this.certificateProvider === CERTIFICATE_PROVIDERS.GIA;
    },
  },
  watch: {
    "$route.query.configuration_id"(val) {
      this.getProductConfiguration().then(() => {
        this.updateJsonSchemaData();
      });
    },
    canSendGoogleAnalyticsStatistic() {
      this.setGaViewItemEvent(this.configurationInfo);
    },
    canSendFBStatistic() {
      this.fbViewContentEvent(this.productData, this.configurationInfo);
    },
    readyToSetRecentlyView: {
      handler(val) {
        if (val) {
          this.setRecentlyViewed({
            price: this.configurationPrice,
            title: this.productTitle,
            image: this.mainImage,
            configuration_id: this.currentSku,
          });
        }
      },
      deep: true,
      immediate: true,
    },
    isForceToPay: {
      async handler(val) {
        if (!this.isOutOfStock && val) {
          this.$store.commit("general/SET_GLOBAL_LOADER", true);
          await this.addToBag();
          this.$store.commit("general/SET_GLOBAL_LOADER", false);
        }
      },
      immediate: true,
    },
    dynamicProductShortDescription() {
      this.descriptionKeyIncrement++;
    },
    configurationInfo: {
      handler(val) {
        if (val) {
          this.checkWishList();
        }
      },
      immediate: true,
    },
  },
  created() {
    this.productData = this.productInfo;
  },
  mounted() {
    this.stickyResizeDiv();
    this.isMounted = true;
  },
  beforeDestroy() {
    this.resizeObserver.unobserve(this.$refs["right-side"]);
  },
  methods: {
    ...mapActions({
      setWishlist: "wishlist/setWishlist",
      setCartInfo: "cart/setCartInfo",
    }),
    ...mapMutations({
      setCompareList: "comparison/SET_COMPARE_LIST",
      toggleCartDrawer: "general/TOGGLE_CART_DRAWER",
    }),

    async toggleWishlist() {
      try {
        const wishlistUniqueId = this.$store.getters.getWishListId;
        let wishlist = JSON.parse(
          JSON.stringify(this.$store.state.wishlist.wishlist)
        );
        if (!wishlist) {
          wishlist = [];
        }
        const exist = wishlist.find(
          (el) => el.sku === this.configurationInfo.configuration_id
        );

        const wishlistEventData = {
          product_price: this.finalConfigurationPriceValue,
          product_title: this.productData?.mixedTranslation?.title,
          product_id: this.productData?.id,
          configuration_id: this.configurationInfo.configuration_id,
          product_configuration_id: this.configurationInfo?.id,
        };

        if (!exist) {
          wishlist.push({
            sku: this.configurationInfo.configuration_id,
            images: this.configurationInfo.medias?.[0]?.file?.url || "",
            slug: this.productData?.slug,
            title: this.productData?.mixedTranslation?.title,
            price: this.finalConfigurationPriceValue,
            embedded: this.productData?.embedded,
            translations: this.productData?.translations,
            engraving: [],
            vendor_model: this.productData?.vendor_model,
            id: this.productData?.id,
            collections_ids:
              this.productData?.collections?.map((item) => item?.id) || [],
            start_at_cost: this.productStartAtPrice,
            configuration_id: this.configurationInfo?.configuration_id,
            product_configuration_id: this.configurationInfo?.id,
            product_id: this.productData?.id,
          });

          this.gaAddToWishlistEvent(wishlistEventData);
          this.fbAddToWishlistEvent(wishlistEventData);

          this.isWishlist = true;
        } else {
          wishlist = wishlist.filter(
            (el) => el.sku !== this.configurationInfo.configuration_id
          );

          this.gaRemoveFromWishlistEvent(wishlistEventData);
          this.fbRemoveFromWishlistEvent(wishlistEventData);

          this.isWishlist = false;
        }
        const { data } = await this.$api.wishlist.addItem({
          unique_id: wishlistUniqueId,
          baggage: wishlist,
        });
        this.setWishlist(data.data.baggage);

        const eventPayload = {
          product_id: this.productData?.id,
          configuration_id: this.productSKU,
          product_sku: this.productData?.vendor_model,
          product_price: this.finalConfigurationPriceValue,
          collection_id: this.productData?.collections?.[0]?.id || "",
          product_configuration_id: this.configurationInfo?.id,
        };

        if (!exist) {
          this.addToFavoritesEvent(eventPayload);
        } else {
          this.removeFromFavoritesEvent(eventPayload);
        }
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    },
    getLocalStorageItem(kayName) {
      return JSON.parse(localStorage.getItem(kayName));
    },
    async toggleCompare() {
      try {
        const comparisonUniqueId =
          this.$store.state.localStorage.comparlist_unique_id;
        let compareList = JSON.parse(
          JSON.stringify(this.$store.state.comparison.compareList)
        );
        if (!compareList) {
          compareList = [];
        }
        const exist = compareList.find(
          (el) => el.sku === this.configurationInfo.configuration_id
        );
        if (!exist) {
          compareList.push({
            sku: this.configurationInfo.configuration_id,
            images: this.configurationInfo.medias?.[0]?.file?.url || "",
            slug: this.productData?.slug,
            title: this.productData?.mixedTranslation?.title,
            price: this.finalConfigurationPriceValue,
            translations: this.productData?.translations,
            engraving: [],
          });
          this.isCompare = true;
        } else {
          compareList = compareList.filter(
            (el) => el.sku !== this.configurationInfo.configuration_id
          );
          this.isCompare = false;
        }
        const { data } = await this.$api.comparison.addItem({
          unique_id: comparisonUniqueId,
          baggage: compareList,
        });
        this.setCompareList(data.data.baggage);
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    },
    stringBuilder(items) {
      return items?.length
        ? items.reduce((acc, item, idx) => {
            if (idx === 0) {
              acc = acc + item?.mixedTranslation?.title;
              return acc;
            }
            acc = acc + ", " + item?.mixedTranslation?.title;
            return acc;
          }, "")
        : "-";
    },
    setMaterials() {
      const result = {};
      const additionalOptions = {};
      this.productData?.material_categories.forEach((el) => {
        const currentMaterials = this.filteredMaterialsByCategories?.length
          ? this.filteredMaterialsByCategories?.find(
              (item) => item.id === el?.id
            )?.materials
          : [];

        const material = currentMaterials?.find((item) =>
          this.configurationInfo?.materials?.includes(item.id)
        );

        if (el?.slug && material && el.sku_enabled) {
          result[el?.slug] = material;
        } else {
          const currentItems = currentMaterials?.find(
            (el) => this.materialsQuery?.some((i) => i === el.sku) || null
          );
          if (currentItems || currentMaterials?.[0]) {
            additionalOptions[el?.slug] =
              currentItems || currentMaterials?.[0] || [];
          }
        }
      });
      this.result = result;
      this.additionalOptions = additionalOptions;
    },
    onFilterClick(categorySlug, item) {
      this.result[categorySlug] = item;

      const newSku = Object.values(this.result).reduce((acc, el) => {
        return acc + "-" + el.sku;
      }, this.productData?.vendor_model);
      this.currentSku = newSku;
    },
    onOptionsFilterClick(categorySlug, item) {
      this.additionalOptions[categorySlug] = item;

      const currentMaterials = this.filteredMaterialsByCategories?.length
        ? this.filteredMaterialsByCategories?.find(
            (item) => item.slug === categorySlug
          )?.materials
        : [];

      const currentQueryMaterial = this.materialsQuery?.find((el) =>
        currentMaterials?.some((i) => i.sku === el)
      );
      if (currentQueryMaterial) {
        const temp = this.materialsQuery.filter(
          (el) => el !== currentQueryMaterial
        );
        temp.push(item.sku);
        this.materialsQuery = temp;
      } else {
        this.materialsQuery = [...this.materialsQuery, item.sku];
      }
    },
    updateJsonSchemaData() {
      // Prepare additional properties for schema.org
      const additionalProperties = this.detailsMapedData?.length
        ? this.detailsMapedData.map((item) => ({
            "@type": "PropertyValue",
            name: item.property,
            value: item.value,
          }))
        : null;

      this.$store.commit("general/SET_JSON_SCHEMA_DATA", {
        pageName: this.productTitle,
        pageImage: getProductImages(this.productData)?.[0]?.url || "",
        product: this.productData,
        productSku:
          this.configurationInfo.configuration_id ||
          this.$route?.query?.configuration_id ||
          "",

        productUrl: `${this.baseUrl}${this.$route.fullPath}`,
        productTitle: this.productTitle,
        productDescription: this.productDescription,
        productPrice: this.finalConfigurationPriceValue,
        productImage: this.configurationInfo?.medias?.find((item) =>
          item.position.includes("PDP")
        )?.file?.url,
        additionalProperties,
        configurationInfo: this.configurationInfo,
        allSelectedMaterials: this.allSelectedMaterials,
        materialCategoriesDictionary: this.materialCategoriesDictionary,
      });
    },
    async getProductConfiguration() {
      try {
        this.configurationLoading = true;
        const { data } = await this.$api.products.getConfiguration(
          this.currentSku
        );
        this.setGaViewItemEvent(data.data);
        this.fbViewContentEvent(this.productData, data.data);
        this.configurationInfo = data.data;
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      } finally {
        this.configurationLoading = false;
        this.configurationLoaded = true;
      }
    },
    async addToBag() {
      if (this.addToBagLoading) {
        return;
      }
      this.addToBagLoading = true;
      try {
        await this.addToBagWithConfiguration(
          this.configurationInfo,
          this.currentSku
        );
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      } finally {
        this.addToBagLoading = false;
        this.$store.commit("general/SET_GLOBAL_LOADER", false);
      }
    },
    async addToBagBySku({ sku, el } = {}) {
      if (!sku || !el || el.classList.contains("is-loading")) {
        return;
      }
      el.classList.add("is-loading");
      try {
        const { data } = await this.$api.products.getConfiguration(sku);
        await this.addToBagWithConfiguration(data.data, sku);
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      } finally {
        el.classList.remove("is-loading");
      }
    },
    async addToBagWithConfiguration(configurationInfo, sku) {
      if (!configurationInfo || !sku) {
        return;
      }
      const imageIdList = configurationInfo?.medias?.reduce((acc, item) => {
        if (item?.position?.includes("PDP") && item?.file?.id) {
          acc.push(item.file.id);
        }
        return acc;
      }, []);
      const imageId = imageIdList?.[0];
      let cartImage = null;
      if (!imageId) {
        cartImage = await mergeImages([
          {
            src:
              configurationInfo?.medias?.[0]?.file?.url ||
              "/images/no-image.webp",
          },
        ]);
        cartImage = DataURIToBlob(cartImage);
      }
      const options = {};
      if (Object.values(this.additionalOptions).length) {
        options.materials = Object.entries(this.additionalOptions).map(
          ([key, val]) => {
            return {
              sku: val.sku,
              title: val?.mixedTranslation?.title,
              material_category_title: this.filteredMaterialsByCategories.find(
                (el) => el.slug === key
              )?.mixedTranslation?.title,
              material_category_id: val.material_category_id,
            };
          }
        );
      }
      const formData = new FormData();
      formData.append("cart_unique_id", this.$store.getters.getCartId);
      formData.append("sku", sku);
      formData.append("vendor_model", this.productData?.vendor_model);
      formData.append("quantity", "1");
      formData.append("options", JSON.stringify(options));
      if (imageId) {
        formData.append("image_id", imageId);
        imageIdList.forEach((id) => {
          formData.append("images_ids[]", id);
        });
      } else {
        formData.append("image", cartImage);
      }
      const { data } = await this.$api.cart.setCartItem(formData);
      await this.setCartInfo(data.data);
      if (this.isForceToPay) {
        await this.$router.replace(
          this.localePath({
            path: `/checkout/${this.cartId}`,
          })
        );
      } else {
        this.$store.commit("general/TOGGLE_CART_DRAWER", true);
      }
      this.gaAddToCartEvent(this.productData, configurationInfo);
      this.fbAddToCartEvent(this.productData, configurationInfo);
      this.addToCartEvent({
        product_id: this.productData.id,
        configuration_id: configurationInfo.configuration_id,
        collection_id: this.productData.collections?.[0]?.id || "",
        product_sku: this.productData.vendor_model,
        product_price:
          configurationInfo.cost ||
          configurationInfo.base_cost ||
          this.configurationPrice,
      });
    },
    setGaViewItemEvent(data) {
      const category = this.productData?.categories?.[0];
      this.gaViewItemEvent(this.productData, data);
      this.gaDynamicParamsEvent("View Product", {
        ecomm_prodid: data.configuration_id,
        ecomm_pagetype: "product",
        ecomm_totalvalue: this.googleAnalyticsPriceFormat(
          data.cost || data.base_cost
        ),
        ecomm_category: category?.mixedTranslation?.title || "",
      });
    },
    stickyResizeDiv() {
      this.customEvent = new Event("pdpResizeEvent");
      this.resizeObserver = new ResizeObserver((entries) => {
        if (this.customEvent) {
          window.dispatchEvent(this.customEvent);
        }
      });
      this.resizeObserver.observe(this.$refs["right-side"]);
    },
    openFilePreview(item) {
      this.$store.dispatch("general/setImageViewer", {
        isOpen: true,
        file: item,
      });
    },
    isHiddenPropertyGroup(propertyGroupSku) {
      if (this.hiddenPropertyGroupByCollection[propertyGroupSku]) {
        return (this.productData?.collections || []).some((collection) => {
          return this.hiddenPropertyGroupByCollection[
            propertyGroupSku
          ].includes(collection.slug);
        });
      } else {
        return false;
      }
    },
    checkWishList() {
      const localStorageWishlist = this.getLocalStorageItem(this.wishlistKey);
      if (
        localStorageWishlist &&
        localStorageWishlist.find(
          (el) => el.sku === this.configurationInfo?.configuration_id
        )
      ) {
        this.isWishlist = true;
      } else {
        this.isWishlist = false;
      }
      const localStorageCompare = this.getLocalStorageItem(this.compareKey);
      if (
        localStorageCompare &&
        localStorageCompare.find(
          (el) => el.sku === this.configurationInfo?.configuration_id
        )
      ) {
        this.isCompare = true;
      } else {
        this.isCompare = false;
      }
    },
  },
};
